import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

// Karmakundali Maroon/Cream Premium Theme
const C = {
  maroon: '#5B1818',
  maroonDark: '#3B0E0E',
  maroonLight: '#7A2727',
  gold: '#C9A23A',
  goldLight: '#E2C170',
  goldSoft: '#F0DCA0',
  cream: '#0B0B0E',
  creamWarm: '#17171C',
  creamDeep: '#101014',
  text: '#F2EDE3',
  textMuted: '#BDB6AB',
  border: 'rgba(201, 162, 58, 0.35)',
  borderStrong: 'rgba(201, 162, 58, 0.55)',
  success: '#E2C170',
  danger: '#E05B5B',
};

const Section = ({ title, children, accent = C.goldLight, bg = C.creamWarm, dangerStyle = false }) => (
  <section
    className="rounded-2xl p-6 sm:p-8 mb-6"
    style={{
      background: dangerStyle ? 'rgba(224,91,91,0.07)' : bg,
      border: `1px solid ${dangerStyle ? 'rgba(178,34,34,0.30)' : C.border}`,
      boxShadow: '0 4px 20px rgba(0,0,0,0.55)',
    }}
  >
    {title && (
      <h3
        className="text-xl sm:text-2xl font-bold mb-4"
        style={{ color: accent, fontFamily: "'Tiro Devanagari Hindi', 'Rozha One', serif" }}
      >
        {title}
      </h3>
    )}
    {children}
  </section>
);

const ReportPage = () => {
  const { leadId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await fetch(`/api/report/${leadId}`);
        const d = await res.json();
        if (d.error) setError(d.error);
        else setData(d);
      } catch (err) {
        setError('Failed to load report');
      }
      setLoading(false);
    };
    fetchReport();
  }, [leadId]);

  const pageStyle = {
    background: '#0B0B0E',
    fontFamily: "'Noto Sans Devanagari', sans-serif",
    color: C.text,
    minHeight: '100vh',
  };

  if (loading) {
    return <div style={pageStyle} className="flex items-center justify-center"><div style={{ color: C.goldLight }} className="text-xl">Loading...</div></div>;
  }
  if (error || !data) {
    return <div style={pageStyle} className="flex items-center justify-center"><div style={{ color: C.danger }} className="text-xl">{error || 'रिपोर्ट नहीं मिली'}</div></div>;
  }

  const sol = data.solution || {};
  const wa = data.whatsapp_target || '9241165807';

  return (
    <div style={pageStyle} data-testid="report-page">
      {/* Header */}
      <header className="py-8 px-4" style={{ background: `linear-gradient(135deg, ${C.maroonDark} 0%, ${C.maroon} 50%, ${C.maroonDark} 100%)`, borderBottom: `2px solid ${C.gold}` }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-3xl mb-2" style={{ color: C.goldLight }}>ॐ</div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: C.goldSoft, fontFamily: "'Tiro Devanagari Hindi', 'Rozha One', serif" }}>माता मलिहा जी</h1>
          <p className="mt-2 text-sm tracking-widest uppercase" style={{ color: C.goldLight, opacity: 0.9 }}>आपकी समस्या की विस्तृत रिपोर्ट</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        {/* Title */}
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: C.goldLight, fontFamily: "'Tiro Devanagari Hindi', 'Rozha One', serif" }}>
            {data.name} जी, आपकी विस्तृत रिपोर्ट
          </h2>
          <p style={{ color: C.textMuted }} className="text-sm tracking-widest">माता मलिहा जी द्वारा व्यक्तिगत विश्लेषण</p>
          <div className="mx-auto mt-4 h-px" style={{ width: 90, background: `linear-gradient(90deg, transparent, ${C.gold}, transparent)` }} />
        </div>

        {/* Submitted Problem */}
        <Section title="आपकी समस्या">
          <p className="leading-relaxed" style={{ color: C.text, fontSize: '16px' }}>{data.problem}</p>
        </Section>

        {/* Divya Darshan - Mata's divine vision (father name + place) */}
        {sol.divya_darshan && (
          <section
            className="rounded-2xl p-6 sm:p-8 mb-6"
            style={{
              background: `linear-gradient(135deg, ${C.maroonDark} 0%, ${C.maroon} 100%)`,
              border: `2px solid ${C.gold}`,
              boxShadow: '0 6px 26px rgba(201,162,58,0.25)',
            }}
            data-testid="divya-darshan-section"
          >
            <div className="text-center mb-4">
              <span className="text-2xl" style={{ color: C.goldLight }}>🔱</span>
              <h3 className="text-xl sm:text-2xl font-bold mt-1" style={{ color: C.goldSoft, fontFamily: "'Tiro Devanagari Hindi', 'Rozha One', serif" }}>
                {sol.divya_darshan_heading || 'माता जी ने ध्यान में क्या देखा'}
              </h3>
              <div className="mx-auto mt-3 h-px" style={{ width: 70, background: `linear-gradient(90deg, transparent, ${C.gold}, transparent)` }} />
            </div>
            <p className="leading-relaxed" style={{ color: '#F7F1E4', fontSize: '16.5px' }}>{sol.divya_darshan}</p>
          </section>
        )}

        {/* Intro */}
        <Section title="भूमिका">
          <p className="leading-relaxed" style={{ color: C.text }}>{sol.intro}</p>
        </Section>

        {/* Bandhan / Root Cause */}
        <Section title="आपकी समस्या का मूल कारण" accent={C.goldLight}>
          <div className="text-center mb-5">
            <div
              className="inline-block px-7 py-4 rounded-xl text-xl sm:text-2xl font-bold"
              style={{
                background: `linear-gradient(135deg, ${C.maroon} 0%, ${C.maroonDark} 100%)`,
                color: C.goldSoft,
                border: `2px solid ${C.gold}`,
                fontFamily: "'Tiro Devanagari Hindi', 'Rozha One', serif",
                letterSpacing: '0.03em',
              }}
              data-testid="bandhan-badge"
            >
              ❖ {sol.bandhan_title || data.bandhan_type} ❖
            </div>
          </div>
          <p className="leading-relaxed mb-4" style={{ color: C.text }}>{sol.what_happens_to_you}</p>
          <div className="rounded-lg p-4" style={{ background: 'rgba(201,162,58,0.07)', borderLeft: `4px solid ${C.gold}` }}>
            <p className="leading-relaxed" style={{ color: C.goldLight, fontSize: '15px' }}>{sol.family_link}</p>
          </div>
        </Section>

        {/* Signs */}
        <Section title={sol.signs_heading}>
          <ul className="space-y-3">
            {(sol.signs || []).map((s, i) => (
              <li key={i} className="flex items-start gap-3 leading-relaxed" style={{ color: C.text }}>
                <span style={{ color: C.gold, marginTop: 4 }}>◆</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
          <p className="leading-relaxed mt-5 italic font-semibold" style={{ color: C.goldLight }}>{sol.signs_close}</p>
        </Section>

        {/* Worst case */}
        <Section title={sol.worst_case_heading} accent={C.danger} dangerStyle>
          <p className="leading-relaxed" style={{ color: C.text, fontSize: '15px', lineHeight: 1.9 }}>{sol.worst_case}</p>
        </Section>

        {/* Why ordinary fails */}
        <Section title="क्यों साधारण उपाय काम नहीं करते?">
          <p className="leading-relaxed" style={{ color: C.text }}>{sol.why_ordinary_fails}</p>
        </Section>

        {/* Sadhana - Solution */}
        <Section title={sol.sadhana_heading} accent={C.success} bg="rgba(201,162,58,0.06)">
          <p className="leading-relaxed mb-5" style={{ color: C.text }}>{sol.sadhana_purpose}</p>
          <h4 className="font-bold mt-5 mb-3" style={{ color: C.goldLight, fontFamily: "'Tiro Devanagari Hindi', serif" }}>7 रात्रि साधना का क्रम:</h4>
          <ol className="space-y-2 list-decimal list-inside" style={{ color: C.text }}>
            {(sol.sadhana_steps || []).map((s, i) => (
              <li key={i} className="leading-relaxed">{s}</li>
            ))}
          </ol>
          <p className="leading-relaxed mt-5" style={{ color: C.text }}>{sol.yantra_section}</p>
        </Section>

        {/* Aghor Mantras */}
        {sol.aghor_mantras && sol.aghor_mantras.length > 0 && (
          <Section title={sol.aghor_mantras_heading || 'अघोर मंत्र — माता जी द्वारा जापित'} accent={C.goldLight} bg="rgba(201,162,58,0.06)">
            <ul className="space-y-3">
              {sol.aghor_mantras.map((m, i) => (
                <li key={i} className="rounded-lg p-3" style={{ background: 'rgba(201,162,58,0.10)', border: `1px solid ${C.border}` }}>
                  <div className="leading-relaxed font-semibold" style={{ color: C.goldLight, fontFamily: "'Tiro Devanagari Hindi', serif" }}>{m}</div>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Tantrik Vidhi */}
        {sol.tantrik_vidhi && sol.tantrik_vidhi.length > 0 && (
          <Section title={sol.tantrik_vidhi_heading || 'तांत्रिक विधि — कैसे होगी साधना'}>
            <ol className="space-y-2 list-decimal list-inside" style={{ color: C.text }}>
              {sol.tantrik_vidhi.map((v, i) => (
                <li key={i} className="leading-relaxed">{v}</li>
              ))}
            </ol>
          </Section>
        )}

        {/* Customer Savdhaniyaan */}
        {sol.customer_savdhaniyaan && sol.customer_savdhaniyaan.length > 0 && (
          <Section title={sol.customer_savdhaniyaan_heading || 'इन 7 दिनों में आपके लिए सावधानियाँ'} accent="#B22222" bg="rgba(178,34,34,0.05)">
            <ul className="space-y-2">
              {sol.customer_savdhaniyaan.map((s, i) => (
                <li key={i} className="flex items-start gap-2 leading-relaxed" style={{ color: C.text }}>
                  <span style={{ color: '#E05B5B' }}>!</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Post-Sadhana / Yantra Dharan Vidhi */}
        {sol.post_sadhana_vidhi && sol.post_sadhana_vidhi.length > 0 && (
          <Section title={sol.post_sadhana_vidhi_heading || 'यंत्र मिलने पर ये विधि करनी है'} accent={C.gold}>
            <ol className="space-y-2 list-decimal list-inside" style={{ color: C.text }}>
              {sol.post_sadhana_vidhi.map((v, i) => (
                <li key={i} className="leading-relaxed">{v}</li>
              ))}
            </ol>
          </Section>
        )}

        {/* Fayde */}
        {sol.fayde && sol.fayde.length > 0 && (
          <Section title={sol.fayde_heading || 'इस साधना से आपको ये विशिष्ट लाभ होंगे'} accent={C.success} bg="rgba(201,162,58,0.06)">
            <ul className="space-y-3">
              {sol.fayde.map((f, i) => (
                <li key={i} className="flex items-start gap-3 leading-relaxed" style={{ color: C.text }}>
                  <span style={{ color: C.success, fontWeight: 'bold' }}>✓</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* Outcomes */}
        <Section title={sol.outcome_heading} accent={C.success} bg="rgba(201,162,58,0.06)">
          <ul className="space-y-3">
            {(sol.outcome || []).map((s, i) => (
              <li key={i} className="flex items-start gap-3 leading-relaxed font-semibold" style={{ color: C.success }}>
                <span style={{ color: C.success }}>✓</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Cost */}
        <Section title={sol.cost_heading} bg={`linear-gradient(135deg, ${C.creamWarm} 0%, ${C.creamDeep} 100%)`}>
          <p className="leading-relaxed mb-5" style={{ color: C.text }}>{sol.cost_intro}</p>
          <table className="w-full">
            <tbody>
              {(sol.cost_breakdown || []).slice(0, -1).map(([label, val], i) => (
                <tr key={i} style={{ borderBottom: `1px dashed ${C.border}` }}>
                  <td className="py-3" style={{ color: C.text }}>{label}</td>
                  <td className="py-3 text-right font-bold" style={{ color: C.goldLight }}>{val}</td>
                </tr>
              ))}
              {sol.cost_breakdown && (
                <tr>
                  <td className="pt-4 font-extrabold text-lg" style={{ color: C.goldLight }}>{sol.cost_breakdown[sol.cost_breakdown.length - 1][0]}</td>
                  <td className="pt-4 text-right font-extrabold text-2xl" style={{ color: C.goldLight }}>{sol.cost_breakdown[sol.cost_breakdown.length - 1][1]}</td>
                </tr>
              )}
            </tbody>
          </table>
        </Section>

        {/* Urgency */}
        <Section title={sol.urgency_heading} accent={C.danger} dangerStyle>
          <p className="leading-relaxed" style={{ color: C.text, fontSize: '15px', lineHeight: 1.9 }}>{sol.urgency}</p>
        </Section>

        {/* Social Proof */}
        <Section title={sol.social_proof_heading}>
          <ul className="space-y-3">
            {(sol.social_proof || []).map((s, i) => (
              <li key={i} className="flex items-start gap-3 leading-relaxed" style={{ color: C.text }}>
                <span style={{ color: C.gold, marginTop: 4 }}>★</span>
                <span>{s}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Final Call */}
        <Section title={null} bg={`linear-gradient(135deg, ${C.creamWarm} 0%, ${C.creamDeep} 100%)`}>
          <p className="leading-relaxed text-center font-semibold" style={{ color: C.text, fontSize: '17px', lineHeight: 1.95 }}>
            {sol.final_call}
          </p>
          <p className="leading-relaxed text-center italic mt-4 text-sm" style={{ color: C.goldLight }}>
            — {sol.guarantee}
          </p>
        </Section>

        {/* CTA Buttons */}
        <div className="text-center my-10">
          {data.payment_status === 'paid' ? (
            <div className="rounded-2xl p-6" style={{ background: 'rgba(201,162,58,0.10)', border: `2px solid ${C.success}` }}>
              <div className="text-4xl mb-3" style={{ color: C.success }}>✓</div>
              <h3 className="text-2xl font-bold mb-2" style={{ color: C.success }}>भुगतान प्राप्त!</h3>
              <p style={{ color: C.success }}>आपकी साधना शुरू हो चुकी है। कृपया धैर्य रखें।</p>
            </div>
          ) : (
            <a
              href={`/payment/${leadId}`}
              data-testid="cta-pay-now"
              className="inline-block text-lg sm:text-xl font-extrabold px-10 py-5 rounded-full transition-all transform hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${C.gold} 0%, ${C.goldLight} 50%, ${C.gold} 100%)`,
                color: '#2A1208',
                border: `1px solid ${C.gold}`,
                boxShadow: '0 6px 24px rgba(201,162,58,0.45)',
                fontFamily: "'Tiro Devanagari Hindi', serif",
                letterSpacing: '0.03em',
              }}
            >
              ❖ अभी साधना शुरू करवाएं — दक्षिणा इच्छानुसार
            </a>
          )}
        </div>

        {/* Disclaimer */}
        <div className="rounded-xl p-4 mt-10 text-center" style={{ background: 'rgba(201,162,58,0.05)', border: `1px solid ${C.border}` }}>
          <p className="text-sm" style={{ color: C.textMuted }}>
            ❖ अघोर विधि से कार्य होता है | पूरी श्रद्धा-विश्वास हो तभी आएं ❖
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 text-center" style={{ background: `linear-gradient(180deg, ${C.maroon} 0%, ${C.maroonDark} 100%)`, borderTop: `2px solid ${C.gold}` }}>
        <div className="text-2xl mb-2" style={{ color: C.gold }}>ॐ</div>
        <p className="font-bold tracking-widest" style={{ color: C.goldSoft }}>जय माता मलिहा</p>
        <p className="text-xs mt-2" style={{ color: C.gold, opacity: 0.7 }}>© 2026 Mata Maliha Ji</p>
      </footer>
    </div>
  );
};

export default ReportPage;
